import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { Pair } from 'src/app/shared/models/main/pair'
import { Word } from 'src/app/shared/models/main/word'
import { environment } from 'src/environments/environment'

export interface Dictionary {
  id: number
  title: string
  words_count: number
  playable_words_count: number
  pairs_count: number
  playable_pairs_count: number
}

export interface GameItem {
  id: number
  content: string
  type: string
}

export interface GameData {
  left_items: GameItem[]
  right_items: GameItem[]
  game_type: 'words' | 'pairs'
  dictionary_id: number
  dictionary_title: string
}

export interface GameMatch {
  left_id: number
  right_id: number
}

export interface GameResults {
  total_matches: number // Total items in the game (correct + unique wrong items)
  correct_matches: number // Number of items matched correctly
  wrong_matches: number // Number of unique items that were wrong
  wrong_items: number[] // Array of unique item IDs that were wrong
  score: number // Percentage score (correct/total * 100)
}

export interface PlayableWordsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Word[]
}

export interface PlayablePairsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Pair[]
}
export interface WeakPoint {
  id: number
  object_id: number
  type: 'word' | 'pair'
  word?: string
  definition?: string
  example?: string
  word_1?: string
  word_2?: string
  dictionaries: string[]
}

@Injectable({
  providedIn: 'root',
})
export class PlayService {
  private readonly API_URL = `${environment.apiUrl}ddc`

  constructor(private http: HttpClient) {}

  getDictionary(dictionaryId: number | string): Observable<any> {
    return this.http.get(`${Backend.HOST_CONTAINER + Backend.PLAY + dictionaryId}/`)
  }

  getPlayableWords(
    dictionaryId: number | string,
    page: number,
    pageSize: number = 10,
  ): Observable<PlayableWordsResponse | null> {
    return this.http.get<PlayableWordsResponse>(
      `${Backend.HOST_CONTAINER + Backend.DIC + dictionaryId}/words/?playable=true&page=${page}&pageSize=${pageSize}`,
    )
  }

  getPlayablePairs(
    dictionaryId: number | string,
    page: number,
    pageSize: number = 10,
  ): Observable<PlayablePairsResponse | null> {
    return this.http.get<PlayablePairsResponse>(
      `${Backend.HOST_CONTAINER + Backend.DIC + dictionaryId}/pairs/?playable=true&page=${page}&pageSize=${pageSize}`,
    )
  }

  startGame(
    dictionaryId: number,
    gameType: 'words' | 'pairs',
    batchSize: number = 4,
  ): Observable<any> {
    const payload = {
      action: 'start_game',
      dictionary_id: dictionaryId,
      game_type: gameType,
      batch_size: batchSize,
    }
    return this.http.post(`${this.API_URL}/play/`, payload)
  }

  submitAnswer(matches: GameMatch[], gameType: 'words' | 'pairs'): Observable<any> {
    const payload = {
      action: 'submit_answer',
      matches,
      game_type: gameType,
    }
    return this.http.post(`${this.API_URL}/play/`, payload)
  }

  submitWrongItems(wrongItems: number[], gameType: 'words' | 'pairs'): Observable<any> {
    const payload = {
      action: 'submit_wrong_items',
      wrong_items: wrongItems,
      game_type: gameType,
    }
    return this.http.post(`${this.API_URL}/play/`, payload)
  }

  getWeakPoints(): Observable<any> {
    const payload = {
      action: 'get_weak_points',
    }
    return this.http.post(`${this.API_URL}/play/`, payload)
  }

  removeWeakPoint(weakPointId: number): Observable<any> {
    const payload = {
      action: 'remove_weak_point',
      weak_point_id: weakPointId,
    }
    return this.http.post(`${this.API_URL}/play/`, payload)
  }

  startWeakPointsGame(weakPoints: WeakPoint[], batchSize: number = 4): GameData | null {
    if (weakPoints.length === 0) {
      return null
    }

    // Take a batch of weak points
    const batch = weakPoints.slice(0, batchSize)
    const gameType = batch[0].type === 'word' ? 'words' : 'pairs'

    const leftItems: GameItem[] = []
    const rightItems: GameItem[] = []

    batch.forEach(wp => {
      if (wp.type === 'word') {
        leftItems.push({
          id: wp.object_id,
          content: wp.word || '',
          type: 'word',
        })

        const content = wp.definition || wp.example || ''
        const type = wp.definition ? 'definition' : 'example'

        rightItems.push({
          id: wp.object_id,
          content,
          type,
        })
      } else if (wp.type === 'pair') {
        leftItems.push({
          id: wp.object_id,
          content: wp.word_1 || '',
          type: 'word_1',
        })

        rightItems.push({
          id: wp.object_id,
          content: wp.word_2 || '',
          type: 'word_2',
        })
      }
    })

    // Shuffle right items
    this.shuffleArray(rightItems)

    return {
      left_items: leftItems,
      right_items: rightItems,
      game_type: gameType as 'words' | 'pairs',
      dictionary_id: 0, // Not applicable for weak points
      dictionary_title: 'Weak Points Practice',
    }
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
  }
}
